import { salidaDelEquipo } from '../utils/salidaDelPortal'
import axios from 'axios'
import { limpiarSesion, alLimpiarSesion, CLAVE_CLINICA_VISITADA } from './sesion'
// t478 · varias cuentas en el mismo dispositivo: una pestaña es de UNA cuenta (utils/cuentasDelDispositivo).
import { cambioLaCuentaPorDebajo, cuentaDeEstaPestana } from '../utils/cuentasDelDispositivo'
import { t, idiomaActivo } from '../utils/i18n'

// B-953 · la sede que este ordenador tiene elegida en la agenda: la MISMA clave que components/SelectorSede (lo vigila
// backend/routes/sedes-cobro-en-la-sede-de-la-cita.test.js). Se lee en cada petición (ver el interceptor).
const CLAVE_SEDE_AGENDA = 'biango.agenda.sede'

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  ENTRAR EN UNA CLINICA QUE NO ES LA TUYA — el gesto, y donde vive
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  EL AGUJERO QUE CIERRA, medido antes de escribirlo. El sistema de accesos estaba entero por
//  dentro: soporte PIDE, la clinica AVISA, alguien AUTORIZA, se VE quien esta dentro y se CORTA de
//  un clic. Todo eso funcionaba. Lo que no existia era el gesto de ENTRAR: `grep -rni 'x-clinica-id'
//  frontend/src` devolvia UNA linea y era un comentario. O sea, la clinica autorizaba una entrada
//  que el soporte no podia usar desde el producto — el servidor concedia y la pantalla no ofrecia,
//  que es la regla de esta casa dicha del reves.
//
//  ── DONDE VIVE EL ESTADO, Y POR QUE AQUI ────────────────────────────────────────────────────
//  En el NAVEGADOR (localStorage, clave declarada en `services/sesion`) y se lee EN CADA PETICION,
//  no en una variable de este modulo. Las dos decisiones importan:
//   · Se guarda → sobrevive a un refresco. Quien esta arreglando una averia recarga la pagina cada
//     dos minutos; si entrar hubiera que repetirlo despues de cada F5, acabaria con dos pestañas
//     abiertas sin saber cual es cual.
//   · Se relee → SALIR ES INMEDIATO y CERRAR SESION LIMPIA DE VERDAD. Con el valor cacheado en
//     memoria, `limpiarSesion()` borraria la clave y este modulo seguiria mandando la clinica de la
//     persona anterior: la siguiente que entrase en ese ordenador arrancaria dentro de la clinica
//     de otro. Releer cuesta una lectura de localStorage por peticion y quita esa clase entera de
//     fallo.
//
//  ── QUIEN LA MANDA: NADIE MAS QUE EL PERSONAL DE PLATAFORMA ─────────────────────────────────
//  La cabecera solo se pone si el usuario que hay en sesion es `es_superadmin`. El servidor ya la
//  ignora para todos los demas (`middleware/auth`: el override ni se lee si no lo eres), pero el
//  candado no puede ser solo del servidor: un usuario normal de clinica no manda NUNCA una cabecera
//  que dice en que clinica trabaja, ni aunque le quedara una clave suelta de otra sesion.
//
//  ── DONDE NO SE MANDA: EL PANEL DE LA PROPIA PLATAFORMA ─────────────────────────────────────
//  `/superadmin/…` y `/acceso-clinica/…` van SIEMPRE sin cabecera, y no es un detalle de comodidad:
//  MEDIDO, con el override puesto la peticion corre dentro del contexto de tenant de la clinica
//  visitada, y `plataforma_accesos` tiene RLS `tenant_isolation`
//  (migracion 2027-04-22-plataforma-accesos.sql). Consecuencia exacta: estando dentro de la clinica
//  7, la lista de mis sesiones esconderia todas las demas clinicas y cerrar la de la clinica 9
//  contestaria «esa sesion ya no existe». Es decir, el panel mentiria sobre su propio estado y LA
//  PUERTA DE SALIDA dependeria de en que clinica estas metido. La cabecera contesta a «¿de que
//  clinica son los datos que estoy mirando?», y esas dos rutas nunca hablan de la clinica visitada:
//  hablan de MIS permisos y de MIS sesiones.
//
//  ── Y SI EL ACCESO SE MUERE MIENTRAS ESTAS DENTRO ───────────────────────────────────────────
//  La sesion caduca sola (ocho horas), o la corta cualquiera del equipo de la clinica de un clic.
//  Cuando eso pasa, el servidor contesta 403 con `codigo: sin_acceso_concedido` (o `sin_seccion`) y
//  su explicacion escrita. Aqui eso NO se deja pasar como un error rojo cualquiera: se suelta la
//  visita —la siguiente peticion ya sale a la clinica propia— y se avisa a quien escuche, para que
//  el aviso de arriba pase de «estas dentro de X» a «el acceso a X ha terminado» con el motivo del
//  servidor. Sin esto, la aplicacion se quedaria pintando datos de una clinica en la que ya no se
//  entra y soltando 403 sin una palabra que los explique.
//  LO QUE NO SOBREVIVE A UN REFRESCO es ese aviso de «ha terminado»: vive en memoria a proposito.
//  Despues de recargar simplemente no estas dentro de nadie, que es la verdad.

const guardarVisita = (v) => {
  try {
    if (v) localStorage.setItem(CLAVE_CLINICA_VISITADA, JSON.stringify(v))
    else localStorage.removeItem(CLAVE_CLINICA_VISITADA)
  } catch (_) { /* modo privado, cuota, etc. */ }
}

// La visita GUARDADA, o null. Nunca lanza: un localStorage revuelto significa «no estoy en ninguna
// clinica», que es el lado seguro.
export function clinicaVisitada() {
  try {
    const j = JSON.parse(localStorage.getItem(CLAVE_CLINICA_VISITADA) || 'null')
    return j && parseInt(j.id) > 0 ? { id: parseInt(j.id), nombre: String(j.nombre || '') } : null
  } catch (_) { return null }
}

function esDePlataforma() {
  try { return JSON.parse(localStorage.getItem('user') || 'null')?.es_superadmin === true }
  catch (_) { return false }
}

// ── EN QUE CLINICA SE ESTA: LA VISITA, O LA DE LA CUENTA ────────────────────────────────────
//
//  Hasta la CUENTA UNICA, «mi clinica» y «la del dominio» eran lo mismo y nadie tenia que
//  preguntarlo: un dominio, una clinica. Desde que el mismo correo trabaja en varias (el login
//  enseña tarjetas y se elige), el dominio YA NO identifica la sesion — la misma persona, el mismo
//  navegador y el mismo dominio pueden ser hoy la clinica A y dentro de un minuto la B.
//
//  Y eso se paga donde mas duele: la marca (nombre, logo y color) la resolvia el DOMINIO, asi que
//  quien entraba en su clinica demo veia el rotulo, el logo y el color de su OTRA clinica por toda
//  la aplicacion —cabecera, panel de administracion, y el membrete de los PDF—. Lo vio el dueño el
//  2-sep: «he entrado a una clinica demo y al entrar al panel de administracion me mete al panel de
//  la otra clinica de mi cuenta».
//
//  La respuesta vive AQUI, en la misma puerta que decide la cabecera, para que no haya dos criterios
//  de «donde estoy» que puedan contestar cosas distintas. El orden importa: la VISITA manda (si
//  alguien de soporte esta dentro de una clinica cliente, esa es la clinica de todo lo que ve), y si
//  no hay visita, la de la cuenta con la que se entro (`user.clinica_id`, que sirve el login).
export function clinicaDeSesion() {
  // t548 · LA DE ESTA PESTAÑA. Si otra pestaña ha cambiado la clinica del navegador (ha entrado en otra clinica o ha
  // cambiado de cuenta) y esta no se ha recargado, esta sigue PINTANDO la de antes: lo que apunte en este ordenador (un
  // borrador, un envio de la cola) es de esa, no de la de la otra pestaña. Ver «CADA PESTAÑA TRABAJA EN LA CLINICA QUE
  // PINTA», mas abajo. Con la visita terminada en esta pestaña (`finVisita`), como siempre: la del navegador.
  const enElNavegador = clinicaDelNavegador()
  const pintada = clinicaDeEstaPestana()
  return pintada && pintada !== enElNavegador && !finVisita ? pintada : enElNavegador
}
function clinicaDelNavegador() {
  // LA VISITA CUENTA CON LAS MISMAS TRES CONDICIONES CON LAS QUE SALE LA CABECERA (ver el
  // interceptor): si quien mira NO es personal de plataforma, su peticion no lleva la cabecera y el
  // servidor le contesta por SU clinica — asi que su clinica es la suya, aunque en ese ordenador
  // haya quedado pegada la clave de una visita de otra sesion. Dos criterios distintos de «donde
  // estoy» es como esto se rompe: uno diria la clinica visitada y el otro la propia.
  const v = esDePlataforma() ? clinicaVisitada() : null
  if (v) return v.id
  try {
    const c = parseInt(JSON.parse(localStorage.getItem('user') || 'null')?.clinica_id)
    return c > 0 ? c : null
  } catch (_) { return null }
}

// ── ¿ESTA SESION ES UNA VISITA DEL SOPORTE A OTRA CLINICA? (B-504) ─────────────────────────────
//
//  El servidor ya no deja escribir a una visita (services/soporteSoloLee). Esta pregunta es para la
//  PANTALLA, que no tiene que ofrecer ni disparar sola lo que el servidor va a negar: dar avisos por
//  vistos, el globo de errores de envio que se cierra para toda la clinica. Las mismas condiciones con
//  las que sale la cabecera —hay visita guardada y quien esta en sesion es personal de plataforma— y
//  una mas, la del servidor: entrar en la clinica PROPIA no es una visita. Sin saber cual es la propia,
//  se contesta que SI: lo que se deja de hacer en una visita es no marcar nada, que es el lado seguro.
export function enVisitaDelSoporte() {
  const v = esDePlataforma() ? clinicaVisitada() : null
  if (!v) return false
  try {
    const propia = parseInt(JSON.parse(localStorage.getItem('user') || 'null')?.clinica_id)
    return !(propia > 0 && propia === v.id)
  } catch (_) { return true }
}

// ══ t548 (29-oct-2026) · CADA PESTAÑA TRABAJA EN LA CLINICA QUE PINTA ═════════════════════════════════════════════
//
//  EL AGUJERO, MEDIDO (backend/routes/pestana-pinta-su-clinica.test.js). La clinica de cada peticion sale del NAVEGADOR —la
//  visita del soporte y el token viven en `localStorage`, que es de todas las pestañas—, y lo que hay en pantalla es de UNA
//  pestaña. Dos casos reales:
//   · EL SOPORTE tiene la ficha de un paciente de la clinica 7 en una pestaña y en otra entra en la 8 (Superadmin → Soporte
//     → «Abrir su clinica»). La primera, sin recargarse, sigue pintando la 7 y su «Guardar» sale con `X-Clinica-Id: 8`: un
//     201 y la fila EN LA 8. Y sus lecturas traen datos de la 8 debajo de la ficha de la 7.
//   · QUIEN TRABAJA EN VARIAS CLINICAS cambia de clinica en otra pestaña: esta manda ya el token de la otra. Solo la paraba
//     la recarga del aviso entre pestañas (components/CuentasGlobales), y un aviso se puede perder.
//
//  LA REGLA. Esta pestaña recuerda la clinica que PINTA: la del navegador cuando arranco (o cuando empezo a tener sesion),
//  y la que ELLA MISMA elige despues (entrar en una visita, salir, entrar con otra cuenta). Lo que cambie otra pestaña no
//  la mueve. En cada peticion:
//   · se compara con la clinica a la que IRIA la peticion (la visita, o la de la cuenta): si no es la misma, no sale —se
//     para aqui, con su motivo, y se avisa a la pantalla (components/PestanaDeOtraClinica: «Esta pestaña es de otra
//     clinica» y «Recargar esta pestaña»)—;
//   · y si sale, lleva `X-Clinica-Pintada`, para que el SERVIDOR haga la misma comprobacion (middleware/auth: 409
//     `pestana_de_otra_clinica`) aunque un dia esta se equivoque.
//  Lo que es de la PERSONA o del panel de Biango (`vaSinCabecera`) va siempre a su clinica propia: se compara con la propia.
//  Recargar es la salida, y es la garantia: la pestaña vuelve a arrancar con la clinica de ahora y nada de la de antes.
let pintadaPropia = null   // la clinica de la cuenta con la que trabaja esta pestaña
let pintadaVisita = null   // la clinica que esta pestaña pinta DENTRO de una visita del soporte (o null)
let pintadaCuenta = null   // la cuenta con la que se tomo (para seguir a la de la pestaña: ver `alDiaConLaCuenta`)
// La visita solo cuenta para el personal de plataforma, con las mismas condiciones que la cabecera: a cualquier otra cuenta
// el servidor le contesta por SU clinica aunque en el navegador haya quedado pegada la clave de una visita.
const visitaPintada = () => (esDePlataforma() ? pintadaVisita : null)
const hayTokenGuardado = () => { try { return !!localStorage.getItem('token') } catch (_) { return false } }
function clinicaPropiaGuardada() {
  try {
    const c = parseInt(JSON.parse(localStorage.getItem('user') || 'null')?.clinica_id)
    return c > 0 ? c : null
  } catch (_) { return null }
}
// Toma la clinica del navegador como la de esta pestaña. Solo cuando esta pestaña todavia no pinta ninguna.
function fijarLaPintada() {
  if (!hayTokenGuardado()) return false
  const propia = clinicaPropiaGuardada()
  if (!(propia > 0)) return false
  const v = esDePlataforma() ? clinicaVisitada() : null
  pintadaPropia = propia
  pintadaVisita = v ? v.id : null
  pintadaCuenta = idDeLaCuentaGuardada()
  return true
}
function soltarLaPintada() { pintadaPropia = null; pintadaVisita = null; pintadaCuenta = null }
const idDeLaCuentaGuardada = () => { try { return parseInt(JSON.parse(localStorage.getItem('user') || 'null')?.id) || null } catch (_) { return null } }
// LA CUENTA DE ESTA PESTAÑA LA DECLARA ELLA MISMA (utils/cuentasDelDispositivo · fijarCuentaDeEstaPestana, al entrar en
// ella: context/AuthContext · login). Si esta pestaña ha pasado a la cuenta que ahora tiene el navegador, su clinica es la
// de esa cuenta. Lo que cambie OTRA pestaña no la declara aqui, asi que no la mueve.
function alDiaConLaCuenta() {
  let c = null
  try { c = cuentaDeEstaPestana() } catch (_) { c = null }
  if (c && pintadaPropia > 0 && c !== pintadaCuenta && c === idDeLaCuentaGuardada()) fijarLaPintada()
}
fijarLaPintada()

/** La clinica que pinta esta pestaña (la visita, o la de la cuenta), o null si todavia no pinta ninguna. */
export function clinicaDeEstaPestana() {
  alDiaConLaCuenta()
  if (!(pintadaPropia > 0) && !fijarLaPintada()) return null
  return visitaPintada() || pintadaPropia
}

// Con que clinica se compara una peticion: lo de la persona y el panel de Biango, con la propia; lo demas, con la que pinta.
function pintadaParaLaPeticion(url) {
  if (!hayTokenGuardado()) return null
  alDiaConLaCuenta()
  if (!(pintadaPropia > 0) && !fijarLaPintada()) return null
  const v = visitaPintada()
  return (!vaSinCabecera(url) && v) ? v : pintadaPropia
}
// A que clinica iria la peticion: las mismas tres condiciones con las que sale la cabecera de la visita (ver el interceptor).
function destinoDeLaPeticion(url) {
  const v = clinicaVisitada()
  if (v && esDePlataforma() && !vaSinCabecera(url)) return v.id
  return clinicaPropiaGuardada()
}

/** Si esta pestaña se ha quedado en otra clinica (otra pestaña ha cambiado la del navegador): { pintada, ahora, nombre }. */
export function pestanaDeOtraClinica() {
  if (!hayTokenGuardado()) return null
  alDiaConLaCuenta()
  if (!(pintadaPropia > 0)) return null
  // Una visita terminada (cortada, caducada o cerrada en otra pestaña) la cuenta la banda de la visita: no son dos avisos.
  if (finVisita) return null
  const pintada = visitaPintada() || pintadaPropia
  const v = esDePlataforma() ? clinicaVisitada() : null
  const ahora = v ? v.id : clinicaPropiaGuardada()
  if (!(ahora > 0) || ahora === pintada) return null
  return { pintada, ahora, nombre: v ? v.nombre : '' }
}

export const MOTIVO_PESTANA_DE_OTRA_CLINICA = 'pestana_de_otra_clinica'
const EVENTO_PESTANA_DE_OTRA_CLINICA = 'biango:pestana-de-otra-clinica'
function anunciarPestanaDeOtraClinica() {
  // Sin ventana (las guardias ejecutan este fichero en node) no hay evento: avisa igual a quien escuche la visita.
  try { window.dispatchEvent(new CustomEvent(EVENTO_PESTANA_DE_OTRA_CLINICA)) } catch { }
  avisar()
}
function marcarPestanaDeOtraClinica(e) {
  e.codigo = MOTIVO_PESTANA_DE_OTRA_CLINICA
  // La cola de envios (services/colaEnvios) lo guarda para la sesion de esa clinica, igual que el 409 del servidor.
  e.motivo = 'otra_sesion'
  // La marca que lee `utils/errores`: aqui no ha fallado ninguna red, y sin ella se diria «Sin conexion».
  e.esPestanaDeOtraClinica = true
  return e
}
function errorPestanaDeOtraClinica(config) {
  const e = new Error(t('services.api.pestana_de_otra_clinica',
    'Esta pestaña es de otra clínica: recárgala. En otra pestaña de este navegador se ha entrado en otra clínica (o con otra cuenta), así que esto NO se ha enviado: se habría guardado en la clínica equivocada.'))
  e.config = config
  return marcarPestanaDeOtraClinica(e)
}

// ── 6-OCT-2026 · EL NIVEL 3 DE SOPORTE TIENE ACCESO TOTAL, COMO LA ADMINISTRACIÓN DE LA CLÍNICA ─────────────────────
//  Quien decide es el servidor, en cada petición (services/soporteSoloLee). La pantalla solo necesita saber si ESTA visita es
//  de nivel 3 para no esconderle lo que el servidor sí le deja hacer (la Nube, las migraciones, lo personal…). Lo apunta la
//  banda de la visita (components/DentroDeClinica) con lo que contesta GET /acceso-clinica/mi-nivel, para ESA clínica. Sin
//  saberlo todavía, en otra clínica o con otra persona en sesión, NO es el 3: se pinta lo de los niveles 1 y 2 y el servidor
//  contesta igual.
const CLAVE_NIVEL_VISITA = 'biango.nivelSoporteVisita'
const yoEnSesion = () => { try { return parseInt(JSON.parse(localStorage.getItem('user') || 'null')?.id) || null } catch (_) { return null } }
export function apuntarNivelDeLaVisita(clinicaId, nivel) {
  try { localStorage.setItem(CLAVE_NIVEL_VISITA, JSON.stringify({ clinica: parseInt(clinicaId), nivel: parseInt(nivel), quien: yoEnSesion() })) }
  catch (_) { /* sin almacenamiento: la pantalla pinta lo de los niveles 1 y 2, y el servidor decide igual */ }
}
export function soporteConAccesoTotal() {
  if (!enVisitaDelSoporte()) return false
  try {
    const v = clinicaVisitada()
    const j = JSON.parse(localStorage.getItem(CLAVE_NIVEL_VISITA) || 'null')
    return !!(v && j && j.clinica === v.id && j.nivel === 3 && j.quien != null && j.quien === yoEnSesion())
  } catch (_) { return false }
}


// El panel de Biango nunca pregunta por la clinica visitada. Ver el parrafo de arriba.
export const RUTAS_SIN_CLINICA = ['/superadmin', '/acceso-clinica']

// ── B-504 · LO QUE ES DE LA PERSONA, O DEL APARTADO DE BIANGO, TAMPOCO LLEVA LA CABECERA ────────
//
//  Desde B-504 una visita del soporte SOLO MIRA: el servidor corta en requireAuth todo lo que escribe
//  con la cabecera puesta (services/soporteSoloLee). Lo que de verdad es de la persona o de Biango no
//  habla de la clinica visitada, y con la cabecera ademas corria con la RLS de ESA clinica: su fila de
//  `agentes` no se veia (las preferencias contestaban 200 sin guardar, la contraseña de la pantalla de
//  bloqueo daba «no es correcta»), el movil se daba de alta para los avisos de la clinica del cliente, y
//  crear una cuenta del equipo de plataforma chocaba con la RLS. Sin cabecera va a SU clinica, que es
//  donde vive todo eso.
//   · POR PREFIJO: su sesion (`/auth`), sus notificaciones del movil (`/push`) y el apartado de Biango.
//   · SOLO POR RUTA EXACTA: lo suyo que vive DENTRO de routers de la clinica. `/notif-prefs/rol`, el
//     desbloqueo de otra persona o las huellas de otros son de la clinica, asi que aqui no cabe un prefijo.
//  Y como son de la persona, estas rutas tambien siguen abiertas con la visita terminada (ver
//  `esSalidaSiempreAbierta`): con el acceso cortado, desbloquear la pantalla o renovar la sesion no
//  pueden quedarse parados en el navegador.
export const RUTAS_DE_LA_PERSONA = ['/auth', '/push', '/biango-avisos', '/ayuda/admin',
  '/consentimientos-oficiales', '/portal-contenidos', '/equipo-plataforma']
export const RUTAS_EXACTAS_DE_LA_PERSONA = ['/notif-prefs',
  '/seguridad/desbloqueo', '/seguridad/desbloqueo/pin', '/seguridad/desbloqueo/pin/verificar',
  '/seguridad/huella/estado', '/seguridad/huella/reto', '/seguridad/huella/verificar',
  '/seguridad/huella/registro/inicio', '/seguridad/huella/registro/fin']
const sinDominio = (url) => String(url || '').replace(/^https?:\/\/[^/]+/, '').replace(/^\/api(?=\/|$)/, '')
export function vaSinCabecera(url) {
  const r = sinDominio(url)
  if (RUTAS_SIN_CLINICA.some(p => r === p || r.startsWith(p + '/'))) return true
  if (RUTAS_DE_LA_PERSONA.some(p => r === p || r.startsWith(p + '/'))) return true
  return RUTAS_EXACTAS_DE_LA_PERSONA.includes(r.split(/[?#]/)[0])
}

// LAS PUERTAS QUE NO SE PUEDEN CERRAR NUNCA, ni siquiera con la visita muerta (ver el candado de
// mas abajo). Son las dos salidas de ese estado:
//  · el panel de la plataforma, que nunca hablo de la clinica visitada — desde ahi se vuelve a pedir
//    acceso y desde ahi se sale;
//  · `/auth/…`. Cerrar sesion NO recarga la pagina (es un cambio de estado de React), asi que el
//    aviso de «se ha terminado» seguiria vivo en memoria: si el candado alcanzara al login, la
//    persona se quedaria encerrada en la pantalla de acceso sin poder volver a entrar.
// Desde B-504 `/auth` va tambien en RUTAS_DE_LA_PERSONA (sin cabecera). Se deja ademas escrito aqui A
// PROPOSITO: la salida del login no puede depender de que nadie retoque esa lista.
function esSalidaSiempreAbierta(url) {
  const r = sinDominio(url)
  return vaSinCabecera(url) || r === '/auth' || r.startsWith('/auth/')
}

// Quien quiera enterarse de que la visita ha empezado, ha terminado o se ha muerto sola.
const oyentes = new Set()
let finVisita = null      // { id, nombre, motivo, mensaje } — solo en memoria, ver arriba
const avisar = () => {
  for (const o of [...oyentes]) {
    try { o(estadoDeVisita()) } catch (_) { /* un oyente roto no puede tumbar a los demas */ }
  }
}

// ══ LA VISITA ES UNA SOLA, Y ES DE TODAS LAS PESTAÑAS ═════════════════════════
//
//  EL AGUJERO, MEDIDO ANTES DE ESCRIBIR NADA, Y ES EL MISMO DAÑO QUE EL BLOQUE DE ABAJO DICE HABER
//  CERRADO — por el lado que no se estaba mirando.
//
//  Quien esta arreglando una averia NO trabaja con una pestaña: tiene la ficha del paciente en una y
//  el panel (o la agenda, o la caja) en otra. Y las dos mitades de este estado vivian en sitios con
//  ALCANCES DISTINTOS: la clave `CLAVE_CLINICA_VISITADA` esta en `localStorage`, que es del NAVEGADOR
//  y lo comparten todas las pestañas; `finVisita` era una variable de MODULO, o sea de UNA pestaña.
//
//  Consecuencia exacta, reproducida con el `services/api` y el axios de verdad: la clinica corta, la
//  pestaña que se lleva el 403 ejecuta `terminarVisita()` — borra la clave COMPARTIDA y se pone su
//  candado PRIVADO. La segunda pestaña no se entera de nada: sigue pintando el nombre y el logo del
//  cliente, y su siguiente «Guardar» sale ya SIN cabecera, o sea a la clinica del SOPORTE, con un
//  201 y sin una palabra. Medido: `{"creadoEn":90}`. Es el mismo registro clinico en la ficha de
//  nadie que documenta el bloque «EL SEGUNDO CLIC», solo que llegando por la otra puerta.
//
//  ── EL MECANISMO, Y POR QUE ESTE ────────────────────────────────────────
//  EL CANDADO SE MUEVE A DONDE VIVE LA CLAVE: se DERIVA de ella. Esta pestaña recuerda lo ultimo que
//  supo de la clave compartida (`visitaVista`), y cada vez que la lee —que es UNA VEZ POR PETICION,
//  porque este modulo ya la releia en cada una a proposito— compara. Si esta pestaña tenia visita y
//  la clave ya no esta, es que se la han quitado por debajo: se pone el candado. No hay ningun
//  mensaje que se pueda perder ni ninguna pestaña que pueda quedarse sin enterarse: la verdad es la
//  clave, y la clave se lee justo antes de decidir.
//
//  El evento `storage` —que el navegador manda a las DEMAS pestañas cuando una toca `localStorage`—
//  va ADEMAS, y solo para una cosa: que la banda de arriba se despinte YA, sin esperar a que esa
//  pestaña haga una peticion. Es comodidad, no correccion; si el navegador no lo manda (o no hay
//  `window`), lo unico que se pierde son unos segundos de banda desactualizada, no la garantia.
//  Un `BroadcastChannel` habria sido una TERCERA copia del estado cuyo unico valor añadido es el
//  texto del motivo — y un mensaje se puede perder; la clave, no.
//
//  ── Y SIN SESION NO SE CANDA NADA ─────────────────────────────────────
//  `limpiarSesion()` borra el token ANTES que la clave de la visita (ese es el orden de
//  `CLAVES_SESION`), asi que cuando la otra pestaña ve desaparecer la visita ya no hay token. Eso NO
//  es un corte, es que se ha cerrado la sesion: candarla ahi solo conseguiria que su siguiente
//  peticion se parase aqui en vez de salir, llevarse el 401 y llevar a esa persona al login. Sin
//  sesion se suelta todo y no se canda nada.
const mismaVisita = (a, b) => (a ? a.id : null) === (b ? b.id : null)
const haySesion = () => { try { return !!localStorage.getItem('token') } catch (_) { return false } }

// Lo ultimo que ESTA pestaña supo de la clave COMPARTIDA. No es una segunda verdad sobre «en que
// clinica estoy» —esa sigue siendo la clave—: es la memoria minima que hace falta para distinguir
// «nunca estuve dentro» de «estaba dentro y me lo han quitado por debajo», que es lo unico que la
// clave, por si sola, no puede contar.
let visitaVista = clinicaVisitada()

function revisarVisitaCompartida() {
  const antes = visitaVista
  const ahora = clinicaVisitada()
  if (mismaVisita(antes, ahora)) return false
  visitaVista = ahora
  // Hay visita otra vez (otra pestaña ha vuelto a entrar): no hay nada muerto que anunciar.
  if (ahora) finVisita = null
  // Habia visita y ya no la hay, con la sesion todavia abierta: a esta pestaña se la han quitado por
  // debajo. El motivo escrito lo tiene la pestaña que se llevo el 403; aqui se dice lo unico que se
  // sabe de cierto, y la banda ya tiene su texto por defecto para ese caso.
  else if (antes && haySesion()) finVisita = { id: antes.id, nombre: antes.nombre, motivo: 'sin_acceso', mensaje: '' }
  else finVisita = null
  avisar()
  return true
}

// SE REVISA AL PREGUNTAR, no solo al pedir: la banda de «estas dentro de X» se pinta con esto, y una
// banda que dice que sigues dentro de una clinica de la que ya te han sacado es justo el dato que no
// se ve. No hay recursion posible: `visitaVista` se actualiza ANTES de avisar, asi que la llamada de
// dentro no encuentra ningun cambio.
export function estadoDeVisita() {
  revisarVisitaCompartida()
  return { dentro: clinicaVisitada(), fin: finVisita }
}
export function suscribirVisita(fn) { oyentes.add(fn); return () => { oyentes.delete(fn) } }

// EL AVISO DEL NAVEGADOR entre pestañas. Solo adelanta el reloj de lo de arriba: sin el, esta pestaña
// se entera en su siguiente peticion; con el, en cuanto la otra suelta la clave.
try {
  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('storage', (e) => {
      // `key` viene a null cuando alguien vacia el almacen entero: eso tambien nos toca.
      if (e && e.key && e.key !== CLAVE_CLINICA_VISITADA) return
      revisarVisitaCompartida()
    })
  }
} catch (_) { /* sin window (pruebas, SSR): la revision de cada peticion sigue siendo la verdad */ }

// ── EL AVISO MUERE CON LA SESION, Y NO PORQUE NADIE SE ACUERDE ──────────────────────────────
//
//  `finVisita` vive en MEMORIA (a proposito: un F5 no tiene que arrastrarlo), asi que `limpiarSesion`
//  —que solo borra claves de `localStorage`— NO lo alcanzaba. Y cerrar sesion no recarga la pagina:
//  es un cambio de estado de React. MEDIDO con el axios de verdad: tras el corte, cerrar sesion y
//  volver a entrar CON OTRA CUENTA —una recepcionista normal de una clinica cualquiera— dejaba sus
//  peticiones paradas AQUI (escrituras y lecturas, status 0) y le pintaba arriba «Se ha terminado tu
//  acceso a CLINICA DEL VECINO»: la aplicacion muerta y, encima, el nombre de otro cliente en la
//  cabecera de la persona que acaba de entrar con sus credenciales.
//
//  NO se resuelve llamando a una funcion desde `logout()`: son TRES sitios (logout, login y el
//  interceptor del 401 de aqui abajo) y el cuarto que se añada mañana se olvidaria, que es
//  exactamente como nacieron los tres restos de sesion que ya documenta `services/sesion`. Se
//  APUNTA en la limpieza unica, y entrar con credenciales vuelve a ser empezar de cero — tambien en
//  esto. `avisar()` va detras para que la banda de arriba se despinte en el mismo gesto: un aviso
//  que se queda escrito despues de limpiarlo es la misma clase de mentira.
//
//  Y SE AVISA SIEMPRE, HAYA HABIDO CORTE O NO — ESTA ERA LA SEGUNDA MITAD, Y NADIE LA MEDIA. El
//  `return` temprano de «si no hay candado, no hay nada que hacer» era falso: con la visita VIVA
//  (nadie ha cortado nada, simplemente se cierra sesion estando dentro) `limpiarSesion()` borra la
//  clave compartida y NO emitia ni un aviso. Y de esos avisos cuelga `hooks/useBranding`, que es la
//  UNICA puerta por la que la interfaz sabe como se llama la clinica. MEDIDO con el `AuthProvider`
//  de produccion: cerrar sesion dentro de la clinica 7 y entrar con OTRA cuenta —una recepcionista
//  normal, que no es de plataforma— la dejaba con «Clinica del Vecino» en la cabecera, su logo, su
//  color y `modulo_asistencias:false`, que es un guardia de ruta; y si `/api/branding-publico` daba
//  un hipo, asi se quedaba TODA la sesion. O sea el nombre y el logo de otro cliente en la pantalla
//  de quien acaba de entrar con sus credenciales.
// t548 · cerrar sesion (o entrar, que empieza limpiando) deja a esta pestaña sin clinica pintada: la toma de la sesion que
// se abra en ella. Va ANTES que la de abajo, que avisa a la pantalla: asi el aviso ya ve la pestaña sin clinica.
alLimpiarSesion(() => { soltarLaPintada() })
alLimpiarSesion(() => {
  finVisita = null
  visitaVista = null
  avisar()
})

/** ENTRAR. Devuelve la visita, o null si no se le ha dicho en que clinica. */
export function entrarEnClinica(clinica) {
  const id = parseInt(clinica && clinica.id)
  if (!(id > 0)) return null
  const v = { id, nombre: String((clinica && clinica.nombre) || '').trim() || ('Clinica ' + id) }
  finVisita = null
  // La memoria de esta pestaña se pone AL DIA ANTES de tocar la clave: si no, el eco del evento
  // `storage` leeria como «cambio de fuera» algo que acaba de hacer ella misma.
  visitaVista = v
  // t548 · entrar es un gesto de ESTA pestaña: desde ahora pinta la clinica en la que entra.
  alDiaConLaCuenta()
  if ((pintadaPropia > 0 || fijarLaPintada()) && esDePlataforma()) pintadaVisita = id
  guardarVisita(v)
  avisar()
  return v
}

/** SALIR. La siguiente peticion ya sale a la clinica propia: no hay nada cacheado que la retenga. */
export function salirDeClinica() {
  finVisita = null
  visitaVista = null
  // t548 · y esta pestaña vuelve a pintar la suya. Sin sesion, ninguna: la toma de la que se abra.
  pintadaVisita = null
  if (!hayTokenGuardado()) soltarLaPintada()
  guardarVisita(null)
  avisar()
}

/**
 * SALIR CERRANDO LA SESION EN EL SERVIDOR (B-998, 1-oct-2026). Es lo que hace el boton «Salir» de la banda.
 *
 * Primero se sale AQUI, y siempre: es inmediato y no depende de la red (la siguiente peticion ya va a la clinica
 * propia). Despues se le dice al servidor que cierre la sesion de soporte en esa clinica, para que la clinica deje de
 * ver «el soporte de Biango esta dentro» y para que con esa cabecera ya no se pueda leer nada. Si esa llamada falla, no
 * pasa nada: ya se ha salido, y la sesion se apaga sola a su hora. Devuelve la clinica de la que se ha salido, o null.
 */
export function salirDeClinicaCerrandoSesion() {
  const v = clinicaVisitada()
  salirDeClinica()
  if (!v) return Promise.resolve(null)
  // Ya se ha salido; si el aviso al servidor falla, la sesion caduca sola.
  return api.post('/acceso-clinica/salir', { clinicaId: v.id }).then(() => v, () => v)
}

// El acceso se ha muerto estando dentro (caducado, revocado o retirada la seccion). No es un error
// de red: es un cambio de estado del que la aplicacion tiene que enterarse y que tiene que decir.
function terminarVisita(datos) {
  const v = clinicaVisitada()
  if (!v) return
  visitaVista = null
  finVisita = {
    id: v.id,
    nombre: v.nombre,
    motivo: (datos && datos.motivo) || 'sin_acceso',
    // EL TEXTO ES EL DEL SERVIDOR, no uno inventado aqui: el servidor distingue «te lo han cortado
    // o ha caducado» de «te han quitado la seccion», y quien lo lea tiene a un cliente esperando.
    mensaje: String((datos && datos.error) || ''),
  }
  // La clave se suelta con el candado YA puesto, por lo mismo que en `entrarEnClinica`.
  guardarVisita(null)
  avisar()
}

// ── Y DESPUES DEL CORTE: LO QUE ESTABA ABIERTO NO SE SIGUE GUARDANDO COMO SI NADA ───────────
//
//  EL AGUJERO, MEDIDO. Cuando la clinica corta el acceso, el primer «Guardar» se lleva el 403 de
//  abajo y `terminarVisita` suelta la visita — eso esta bien. Pero la aplicacion sigue ENTERA Y
//  OPERATIVA sobre la clinica PROPIA, con el formulario de la OTRA clinica abierto en pantalla. La
//  segunda pulsacion del mismo boton —«me ha dado error, lo intento otra vez», que es la reaccion
//  natural de cualquiera— sale ya SIN cabecera: el registro se crea en la clinica del soporte, con
//  un 201 y sin una palabra. Datos clinicos de un paciente que no es de ahi, escritos en la ficha
//  de nadie, y sin nada en pantalla que lo cuente.
//
//  LA REGLA, ENTONCES: mientras la visita esta MUERTA y sin reconocer, las peticiones que HABRIAN
//  llevado la cabecera no se redirigen a la clinica propia — se paran aqui, con el motivo escrito.
//  No es un candado del servidor (el servidor haria bien su trabajo: es TU clinica y tienes permiso
//  de sobra), es que la peticion ha dejado de significar lo que quien la pulsa cree que significa.
//
//  QUE SI PASA: `esSalidaSiempreAbierta` — `/superadmin/…` y `/acceso-clinica/…`, que nunca hablaron
//  de la clinica visitada sino de MIS permisos y MIS peticiones, y `/auth/…`. Sin lo primero, el
//  propio panel desde el que se vuelve a pedir acceso se quedaria tambien mudo y la salida
//  dependeria de lo que se acaba de romper; sin lo segundo, cerrar sesion —que NO recarga la
//  pagina— dejaria a la persona encerrada en la pantalla de acceso sin poder volver a entrar.
//
//  COMO SE SALE: recargando («Volver a mis datos», el boton de la banda). `finVisita` vive solo en
//  memoria, asi que al recargar simplemente no estas dentro de nadie, que es la verdad.
export const MOTIVO_VISITA_TERMINADA = 'visita_terminada'

function errorVisitaTerminada(fin, config) {
  const e = new Error(t('services.api.tu_acceso_a_ha_terminado', 'Tu acceso a {{x}} ha terminado, así que esto NO se ha enviado: se habría guardado en tu propia clínica y no en la de ese paciente. Pulsa «Volver a mis datos» en el aviso de arriba para seguir trabajando con lo tuyo.', { x: ((fin && fin.nombre) || t('services.api.esa_clinica', 'esa clínica')) }))
  e.codigo = MOTIVO_VISITA_TERMINADA
  // La marca que lee `utils/errores`: sin ella, el mensaje que se le enseñaria a la persona seria
  // «Sin conexión con el servidor», que es una averia inventada — aqui no ha fallado ninguna red.
  e.esVisitaTerminada = true
  e.config = config
  return e
}

// ── Y LA CONFIGURACION DE LA CLINICA TAMBIEN PASA POR AQUI ──────────────────────────────────
//
//  EL AGUJERO, MEDIDO. `hooks/useBranding` pedia `/api/config-cliente` con `fetch` CRUDO, o sea sin
//  pasar por este interceptor y por tanto SIN la cabecera de abajo. Consecuencia exacta, con el
//  soporte dentro de la clinica 7: el servidor contestaba por la clinica PROPIA. Y esa respuesta no
//  es estetica —reparte el NOMBRE que sale en la cabecera del menu, en el pie del panel de
//  administracion, en la cabecera de los PDF de Informes, en el historial impreso del paciente, en
//  el correo de pedido de material y en las etiquetas del almacen— y ademas dos BANDERAS DE
//  COMPORTAMIENTO: `modulo_asistencias`, que es un guardia de ruta, y `correo_pacientes`, que decide
//  si sale el boton de enviar por correo y con que identidad. Todo eso salia de la clinica del
//  soporte mientras se trabajaba dentro de otra: documentos con el membrete de una empresa y los
//  datos de otra.
//
//  LA REGLA: la identidad y la configuracion de «en que clinica estoy» NO se piden por fuera de
//  este modulo. Una segunda puerta con su propia copia del criterio es como se vuelve a romper —
//  el dia que cambie la condicion de abajo, la copia se queda vieja y nadie se entera.
const api = axios.create({
  baseURL: '/api',
  timeout: 120000,
})

api.interceptors.request.use((config) => {
  // t478 (26-oct-2026) · UNA PESTAÑA, UNA CUENTA. Con varias cuentas abiertas en el dispositivo (la de la clínica y la de
  // Biango, como Gmail), si OTRA pestaña pasa a otra cuenta, esta seguiría enseñando la pantalla de la anterior y mandando
  // el token de la nueva: datos de una cuenta en la pantalla de otra. Antes de mandar nada se mira; si ha cambiado, no se
  // manda y la pestaña se recarga con la cuenta de ahora.
  if (cambioLaCuentaPorDebajo()) {
    try { window.location.reload() } catch (_) { /* sin ventana */ }
    const e = new Error(t('services.api.has_cambiado_de_cuenta_en', 'Has cambiado de cuenta en otra pestaña: esta se recarga con la cuenta de ahora. No se ha enviado nada.'))
    e.codigo = 'cuenta_cambiada'
    e.config = config
    throw e
  }
  const token = localStorage.getItem('token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  // B-953 · LA SEDE ELEGIDA EN PANTALLA (11-oct-2026). Con varias sedes, lo que se cobra sin cita entra en la caja de la
  // sede que este ordenador tiene elegida en la agenda (components/SelectorSede, la misma clave). El servidor la comprueba
  // (que sea de la clínica, abierta y que la persona la pueda mirar: services/sedeDelCobro) y, si no vale, no la usa.
  // «Todas» o nada elegido: no se manda y el cobro va a la sede de quien cobra, como antes.
  try {
    const s = localStorage.getItem(CLAVE_SEDE_AGENDA)
    if (s && /^\d{1,9}$/.test(s)) config.headers['X-Sede-Pantalla'] = s
  } catch (_) {
    // sin almacenamiento: sin sede de pantalla
  }
  // t605 · B-2259 · LOS MENSAJES DEL SERVIDOR, EN EL IDIOMA DE ESTA PANTALLA. Solo si no es el castellano, que solo pasa con
  // la función «idiomas» encendida (components/idiomas/IdiomaDeLaSesion); el servidor lo vuelve a mirar con la clínica de la
  // sesión (backend/services/i18n · idiomaDeRes). En castellano la petición sale igual que siempre.
  try {
    const idi = idiomaActivo()
    if (idi && idi !== 'es') config.headers['X-Biango-Idioma'] = idi
  } catch (_) {
    // sin idioma: en castellano
  }
  // LA CLAVE ES DE TODAS LAS PESTAÑAS, Y SE MIRA JUSTO ANTES DE DECIDIR. Si otra pestaña ha soltado
  // la visita (le cortaron el acceso, o salio por las buenas), esta se entera AQUI aunque no le haya
  // llegado ningun evento: sin esta linea, su siguiente «Guardar» sale sin cabecera, o sea a la
  // clinica del soporte, con un 201 y sin una palabra.
  revisarVisitaCompartida()
  // Ver el parrafo de arriba: con la visita ya terminada, esto NO se manda a la clinica propia.
  if (finVisita && !esSalidaSiempreAbierta(config.url)) throw errorVisitaTerminada(finVisita, config)
  // t548 · LA PETICION VA A LA CLINICA QUE ESTA PESTAÑA PINTA, O NO VA. Ver «CADA PESTAÑA TRABAJA EN LA CLINICA QUE
  // PINTA», arriba: si otra pestaña ha cambiado la clinica del navegador, esto no sale (ni lectura ni escritura) y la
  // pantalla lo dice. Si sale, el servidor lo comprueba otra vez con esta cabecera.
  const pintada = pintadaParaLaPeticion(config.url)
  if (pintada) {
    if (destinoDeLaPeticion(config.url) !== pintada) {
      anunciarPestanaDeOtraClinica()
      throw errorPestanaDeOtraClinica(config)
    }
    config.headers['X-Clinica-Pintada'] = String(pintada)
  }
  // LA CABECERA QUE CAMBIA LA CLINICA DE ESTA PETICION. Las tres condiciones van juntas a
  // proposito: hay una visita guardada, quien esta en sesion es personal de plataforma, y la ruta
  // no es la del propio panel.
  const visita = clinicaVisitada()
  if (visita && esDePlataforma() && !vaSinCabecera(config.url)) {
    config.headers['X-Clinica-Id'] = String(visita.id)
    // La marca de que ESTA peticion salio desde dentro de una clinica. Se lee en la respuesta para
    // no confundir un 403 de la visita con cualquier otro 403 de la aplicacion.
    config.dentroDeClinica = visita.id
  }
  // ── «VER COMO» TAMBIEN EN LOS DATOS (B-036) ────────────────────────────────────────────────
  //  El dueño: «si veo la app como otro rol con el boton de cabecera, las agendas y seguro que
  //  otras cosas de la app tampoco se ven como ese rol correctamente, SIGO VIENDO LAS AGENDAS QUE
  //  TENGA ASIGNADAS A MI USUARIO/ROL».
  //
  //  Y tenia razon: hasta hoy la previsualizacion solo cambiaba el gating VISUAL de esta pantalla
  //  (menus, rutas, pestañas). El servidor no se enteraba —su JWT sigue siendo el del
  //  administrador— asi que las LISTAS seguian llegando completas. O sea que se podia mirar la
  //  aplicacion «como Laura» y ver las agendas de uno mismo: una previsualizacion que no
  //  previsualiza es peor que no tenerla, porque se confia en ella.
  //
  //  ESTO NO ABRE NADA. La cabecera solo puede RECORTAR, y el servidor la acepta unicamente si
  //  quien entra ya es administrador de esa clinica y solo en LECTURAS (ver middleware/auth). El
  //  token no cambia: aqui no se esta suplantando a nadie, se esta pidiendo ver menos.
  //  B-1000: y ademas solo a la cuenta de pruebas del equipo de Biango (superadmin), nunca de visita.
  //  A cualquier otra cuenta el servidor le ignora la cabecera, y AuthContext le borra lo guardado.
  try {
    const vc = JSON.parse(localStorage.getItem('ver_como_usuario') || 'null')
    if (vc && vc.id) config.headers['X-Ver-Como-Usuario'] = String(vc.id)
    else {
      const rol = localStorage.getItem('ver_como_rol')
      if (rol) config.headers['X-Ver-Como'] = String(rol)
    }
    // COMENTARIO DE LINEA Y NO DE BLOQUE, a proposito: este fichero descuadra al quitador ingenuo
    // con el que miden 111 guardias de la casa, y un par nuevo lo empeoraba (medido: api.js pasaba
    // de esconder 13 lineas de codigo a 41, cruzaba el umbral y ponia rojo el trinquete de
    // test-helpers/sinComentarios). Sin previsualizacion, la peticion sale como siempre.
  } catch { }
  // B-873 · LA APP NATIVA DEL EQUIPO SE PRESENTA (X-Biango-App: ios | android). El servidor decide con esto, y con el
  // navegador que trae, desde donde se ficha (services/origenFichaje): ya no se fia de lo que diga la pantalla. Sin
  // `window` (las guardias ejecutan este fichero en node) no se toca nada. Comentarios de linea, ver la nota de arriba.
  try {
    const C = typeof window !== 'undefined' ? window.Capacitor : null
    if (C && typeof C.isNativePlatform === 'function' && C.isNativePlatform()) config.headers['X-Biango-App'] = String((typeof C.getPlatform === 'function' && C.getPlatform()) || 'app')
  } catch { }
  return config
})

// El aviso a quien sepa pedir el código (components/SegundoFactorPanel.jsx). El evento es SINCRONO: si alguien lo atiende,
// lo dice en el acto con `atender()` y contesta despues con `responder(true|false)`. Sin ventana (pruebas) o sin nadie que
// escuche, contesta que no.
export function pedirSegundoFactorReciente(cuerpo) {
  return new Promise((resolve) => {
    let atendido = false
    try {
      window.dispatchEvent(new CustomEvent('biango:segundo-factor-reciente', { detail: { cuerpo, atender: () => { atendido = true }, responder: (ok) => resolve(ok === true) } }))
    } catch (_) { atendido = false }
    if (!atendido) resolve(false)
  })
}

// ── UN 401 SUELTO NO CIERRA LA SESION: SE COMPRUEBA ANTES (t98, 5-oct-2026) ──────────────────────
//  El dueño, en su horario de trabajo: «me está cerrando la sesión automáticamente», y le sacaba a la
//  pantalla de entrar. Hasta hoy CUALQUIER 401 de CUALQUIER llamada borraba la sesión entera, y hay
//  401 que no dicen que la sesion de AHORA este muerta:
//   · EL DE UNA LLAVE VIEJA. Cambiar de clínica, volver de la demo a Biango, cambiar la contraseña o
//     teclear el código de la app ponen una llave NUEVA y cortan la vieja en el servidor. Lo que ya
//     iba de camino con la vieja (el refresco de otra pestaña, una consulta lenta) volvía con 401 y
//     ese 401 borraba la llave NUEVA: fuera, recién entrado.
//   · EL QUE NO ES DE LA SESION: una ruta que contesta 401 por otra cosa (un código o un PIN mal
//     puesto, o una segunda pasada del servidor que no encuentra a quien entra, como la del 3-oct).
//  Lo que hace ahora: si la llave con la que salio la peticion ya no es la guardada, NO se cierra
//  nada (y una lectura se repite con la buena). Si es la misma, se le pregunta al servidor UNA vez
//  si esa sesion sigue viva (POST /auth/renovar, que solo contesta 401 si de verdad no vale), y solo
//  con ese segundo 401 se cierra. Una caida, un 502 del reinicio o un fallo de red al preguntar no
//  echan a nadie. Lo mide routes/sesion-no-se-cierra-por-un-401-suelto.test.js.
const VIVA_COMPROBADA_MS = 30000
let sondeoSesion = null
let sesionVivaComprobada = { token: '', hasta: 0 }

function tokenGuardado() {
  try { return localStorage.getItem('token') || '' } catch (_) { return '' }
}

// La llave con la que SALIO esta peticion (la puso el interceptor de peticiones de arriba).
function tokenDeLaPeticion(config) {
  try {
    const h = config && config.headers
    if (!h) return ''
    const v = typeof h.get === 'function' ? (h.get('Authorization') || h.Authorization) : h.Authorization
    const s = String(v || '')
    return s.startsWith('Bearer ') ? s.slice(7) : ''
  } catch (_) { return '' }
}

// ¿Esta muerta de verdad la sesion de esta llave? true solo si el servidor lo confirma con OTRO 401.
// Sin llave guardada no hay sesion que salvar: true, como siempre. Las preguntas a la vez se juntan
// en una, y una sesion que acaba de contestar viva no se vuelve a preguntar en 30 s.
function sesionMuertaDeVerdad(token) {
  if (!token) return Promise.resolve(true)
  if (sesionVivaComprobada.token === token && Date.now() < sesionVivaComprobada.hasta) return Promise.resolve(false)
  if (sondeoSesion && sondeoSesion.token === token) return sondeoSesion.promesa
  const promesa = axios.post((api.defaults.baseURL || '/api') + '/auth/renovar', null,
    { headers: { Authorization: 'Bearer ' + token }, timeout: 20000 })
    .then(() => { sesionVivaComprobada = { token, hasta: Date.now() + VIVA_COMPROBADA_MS }; return false })
    .catch((e) => {
      const st = e && e.response ? e.response.status : 0
      return st === 401
    })
    .finally(() => { if (sondeoSesion && sondeoSesion.token === token) sondeoSesion = null })
  sondeoSesion = { token, promesa }
  return promesa
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // El 401 de POST /auth/login significa "credenciales incorrectas", NO "sesión muerta". Tratarlo
    // como sesión muerta hacía que una contraseña mal tecleada RECARGARA la página entera (la misma
    // URL asignada a location.href recarga): el mensaje de error no llegaba a verse, el formulario
    // se vaciaba, y el login parecía roto sin decir por qué.
    const esLogin = String(error.config?.url || '').includes('/auth/login')
    if (error.response?.status === 401 && !esLogin) {
      // t98 · LA LLAVE DE ESTA PETICION YA NO ES LA GUARDADA: el 401 es de la vieja (ver arriba de
      // `sesionMuertaDeVerdad`). No se cierra nada; una lectura se repite UNA vez con la buena.
      const usado = tokenDeLaPeticion(error.config)
      const actual = tokenGuardado()
      if (actual && usado !== actual) {
        const metodo = String((error.config && error.config.method) || 'get').toLowerCase()
        if (error.config && !error.config._llaveNueva && (metodo === 'get' || metodo === 'head')) {
          return api({ ...error.config, _llaveNueva: true })
        }
        return Promise.reject(error)
      }
      return sesionMuertaDeVerdad(actual).then((muerta) => {
        // Viva (o no se ha podido saber), o la llave ha cambiado mientras se preguntaba: no se toca.
        if (!muerta || tokenGuardado() !== actual) return Promise.reject(error)
        // LIMPIEZA COMPLETA, la misma que "Cerrar sesión". Antes se borraban solo token y usuario y se
        // dejaba puesto el "ver como": al volver a entrar, la aplicacion arrancaba previsualizando el
        // rol de otra persona y rebotaba al que acababa de entrar con sus credenciales buenas.
        limpiarSesion()
        // NO redirigir al login de staff si el usuario está en el portal de pacientes: allí conviven
        // dos sesiones (staff + portal) y un 401 de una llamada de staff en segundo plano no debe
        // sacar al paciente de /portal. Solo se limpian las credenciales de staff.
        // Y si YA estamos en /login, no hay nada que redirigir: reasignar la misma URL recarga la
        // página debajo del usuario.
        // t264c · lo mismo en el portal del laboratorio (/lab): tiene su sesión y su entrada (/lab/entrar), y nunca se le
        // lleva al login de las clínicas (utils/salidaDelPortal).
        const salida = salidaDelEquipo(window.location.pathname)
        if (salida) window.location.href = salida
        return Promise.reject(error)
      })
    }
    // ── EL ACCESO SE HA MUERTO MIENTRAS ESTABAS DENTRO ────────────────────────────────────────
    // Solo cuenta si ESTA peticion salio desde dentro de una clinica (la marca del interceptor de
    // arriba) y si el 403 es el de la puerta y no otro cualquiera: el historial de accesos tambien
    // contesta 403 a quien no es administrador de la clinica, y eso no es que se haya acabado nada.
    const cuerpo = error.response?.data
    if (error.response?.status === 403 && error.config?.dentroDeClinica && cuerpo
        && (cuerpo.codigo === 'sin_acceso_concedido' || cuerpo.codigo === 'sin_seccion')) {
      terminarVisita(cuerpo)
    }
    // t548 · EL SERVIDOR DICE QUE ESTA PESTAÑA ES DE OTRA CLINICA (middleware/auth): no se ha hecho nada, y se avisa igual
    // que cuando lo para el navegador.
    if (error.response?.status === 409 && cuerpo && cuerpo.codigo === MOTIVO_PESTANA_DE_OTRA_CLINICA) {
      marcarPestanaDeOtraClinica(error)
      anunciarPestanaDeOtraClinica()
    }
    // EL CÓDIGO DE LA APP DE AUTENTICACIÓN (cuentas de Biango; 3 y 5-oct-2026). Superadmin sin el código de las últimas 8
    // horas contesta 403 `segundo_factor_reciente` (nunca 401: la sesión de la clínica sigue viva). Se pide aquí UNA vez
    // (lo pinta components/SegundoFactorPanel.jsx, que escucha el evento) y, con el token nuevo, se repite la MISMA
    // petición. Si nadie escucha o se cancela, sigue el error; y si era DENTRO de otra clínica (la pantalla de la clínica no
    // pide el código), la visita no sigue: se suelta como cuando se corta el acceso.
    if (error.response?.status === 403 && cuerpo && cuerpo.codigo === 'segundo_factor_reciente' && error.config && !error.config._sfReintento) {
      return pedirSegundoFactorReciente(cuerpo).then((ok) => {
        if (ok) return api({ ...error.config, _sfReintento: true })
        if (error.config.dentroDeClinica) terminarVisita(cuerpo)
        return Promise.reject(error)
      })
    }
    // (6-oct-2026) UN MÓDULO QUE SE HA APAGADO MIENTRAS ESTABAS DENTRO (la clínica, Biango o la licencia): el servidor
    // contesta 403 `modulo_apagado`. Se avisa en el acto (main.jsx vuelve a pedir los permisos) y el menú, las tarjetas
    // del inicio de administración y las pantallas de ese módulo desaparecen sin esperar ni recargar.
    if (error.response?.status === 403 && cuerpo && cuerpo.codigo === 'modulo_apagado') {
      try { window.dispatchEvent(new CustomEvent('biango:modulo-apagado')) } catch (_) {}   // sin ventana
    }
    // B-360 · UN PAGO PENDIENTE CON BIANGO: el servidor contesta 402 con `codigo: 'IMPAGO'` y el estado de la clínica. Se
    // avisa a la pantalla (components/AvisoImpago.jsx) en el acto, sin esperar a su próxima pregunta.
    if (error.response?.status === 402 && cuerpo && cuerpo.codigo === 'IMPAGO') {
      try { window.dispatchEvent(new CustomEvent('biango:impago', { detail: cuerpo.impago || null })) } catch (_) {}   // sin ventana
    }
    // t120 · EL ESPACIO DE LA CLÍNICA, LLENO: a una subida, el servidor contesta 413 con `codigo: 'ESPACIO_LLENO'`. Se avisa a
    // la pantalla (components/AvisoEspacioLleno.jsx), que explica qué pasa y cómo ampliar o liberar. El error sigue su curso.
    if (error.response?.status === 413 && cuerpo && cuerpo.codigo === 'ESPACIO_LLENO') {
      try { window.dispatchEvent(new CustomEvent('biango:espacio-lleno', { detail: cuerpo })) } catch (_) {}   // sin ventana
    }
    // t199 · UNA CLÍNICA DADA DE BAJA (backend/services/bajaClinica.js). En sus 30 días para descargar los datos, el servidor
    // contesta 402 `BAJA` a lo demás: se avisa a la pantalla (components/AvisoImpago.jsx) en el acto. Pasados, 403
    // `BAJA_CERRADA` a todo: la sesión ya no sirve, se cierra y la pantalla de entrar dice por qué.
    if (error.response?.status === 402 && cuerpo && cuerpo.codigo === 'BAJA') {
      try { window.dispatchEvent(new CustomEvent('biango:baja', { detail: cuerpo.baja || null })) } catch (_) {}   // sin ventana
    }
    if (error.response?.status === 403 && cuerpo && cuerpo.codigo === 'BAJA_CERRADA' && !esLogin && tokenDeLaPeticion(error.config)) {
      try { sessionStorage.setItem('biango:aviso-entrar', String(cuerpo.error || '')) } catch (_) {}   // sin almacenamiento
      limpiarSesion()
      const salida = salidaDelEquipo(window.location.pathname)
      if (salida) window.location.href = salida
    }
    return Promise.reject(error)
  }
)

export default api
